/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Resource from 'linced-admin/enums/enrolment-log-resource';
import type Person from 'linced-admin/models/person';
import type School from 'linced-admin/models/school';

// Proto: enrolments.Log.
export default class Log extends Model {
  declare [Type]: 'enrolments/log';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // schoolId is the unique identifier of the school.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the unique identifier of the School's Campus.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // jurisdiction maps the audit log to a jurisdictional educational
  // authority.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // resource was the resource the action has been performed on.
  // 1: LogResource_CREDENTIALS
  // 2: LogResource_IDENTIFIERS
  // 3: LogResource_STATUS
  // 4: LogResource_ENROLMENTS
  // 5: LogResource_UNENROLMENTS
  @attr('number', { defaultValue: 0 })
  declare resource: number;

  // action was the action performed on the resource.
  // 1: LogAction_CREATE
  // 2: LogAction_UPDATE
  // 3: LogAction_DELETE
  @attr('number', { defaultValue: 0 })
  declare action: number;

  // statusId may provide a link to the specific status request that was
  // sent to the third party educational provider if further request
  // introspection is required.
  @attr('string', { defaultValue: '' })
  declare statusId: string;

  // requesterId is the person, or staff member who requested this data from
  // the 3rd-party provider.
  @belongsTo('person', { async: true, inverse: null })
  declare requesterId: AsyncBelongsTo<Person>;

  @alias('requesterId')
  declare requester: AsyncBelongsTo<Person>;

  // learnerId may contain a link to the learner the action was performed on
  // behalf of.
  @belongsTo('person', { async: true, inverse: null })
  declare learnerId: AsyncBelongsTo<Person>;

  @alias('learnerId')
  declare learner: AsyncBelongsTo<Person>;

  // resourceId may contain a link to the specific linked resource.
  @attr('string', { defaultValue: '' })
  declare resourceId: string;

  @computed('jurisdiction', 'resource', 'action')
  get actionPerformed() {
    let verb = 'performed ';
    let resource = 'an unknown action ';

    switch (this.action) {
      case 1:
        verb = 'created ';
        break;

      case 2:
        verb = 'updated ';
        break;

      case 3:
        verb = 'deleted ';
        break;

      case 4:
        verb = 'marked ';
    }

    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        switch (this.resource) {
          case Resource.IDENTIFIERS:
            resource = 'a GetCheckNSN ';
            break;

          case Resource.ENROLMENTS:
            resource = 'an Arrivals Upload ';
            break;

          case Resource.UNENROLMENTS:
            resource = 'a Leavers Upload ';
            break;
        }
        break;
    }

    if (this.action === 4) {
      return `${verb} ${resource} as processed`;
    }

    return `${verb} ${resource}`;
  }
}
